import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { FLEET, SITE, RATES } from '../data/fleet'
import { billableDays, priceRental, formatUSD } from '../lib/pricing'
import { sendBookingRequest } from '../lib/api'
import PhotoSlider from '../components/PhotoSlider'

function today() {
  return new Date().toISOString().slice(0, 10)
}

const EMPTY = {
  method: 'pickup', // 'pickup' | 'delivery'
  name: '',
  company: '',
  phone: '',
  email: '',
  start_date: '',
  end_date: '',
  drop_address: '',
  city: '',
  zip: '',
  notes: '',
}

function validate(values, days) {
  const errors = {}

  if (!values.name.trim()) errors.name = 'Who should we ask for?'
  if (values.phone.replace(/\D/g, '').length < 10) {
    errors.phone = 'Enter a 10-digit phone number.'
  }
  if (!/\S+@\S+\.\S+/.test(values.email)) {
    errors.email = 'We send your quote here.'
  }
  if (!values.start_date) errors.start_date = 'Pick a start date.'
  if (!values.end_date) errors.end_date = 'Pick a return date.'
  if (values.start_date && values.end_date && days < 1) {
    errors.end_date = 'Return has to be on or after pickup.'
  }

  // The job site only matters if we're the ones driving it out there.
  if (values.method === 'delivery') {
    if (!values.drop_address.trim()) {
      errors.drop_address = 'We need a street address to set the trailer.'
    }
    if (!values.city.trim()) errors.city = 'Which city?'
    if (!/^\d{5}$/.test(values.zip.trim())) {
      errors.zip = 'Enter a 5-digit ZIP.'
    }
  }

  return errors
}

export default function Reserve() {
  const { unitId } = useParams()
  const trailer = FLEET.find((t) => t.id === unitId)

  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [state, setState] = useState('idle') // idle | sending | sent | error
  const [message, setMessage] = useState(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [unitId])

  const days = billableDays(values.start_date, values.end_date)
  const quote = useMemo(() => priceRental(trailer, days), [trailer, days])
  const delivering = values.method === 'delivery'

  if (!trailer) {
    return (
      <section className="section section--dark">
        <div className="wrap">
          <span className="eyebrow eyebrow--dark">Not found</span>
          <h2>No such unit</h2>
          <p className="hero__sub">
            That trailer link doesn&rsquo;t match anything in the fleet.
          </p>
          <div className="hero__actions">
            <Link className="btn btn--primary" to="/">
              See the fleet
            </Link>
          </div>
        </div>
      </section>
    )
  }

  function update(field) {
    return (e) => {
      const { value } = e.target
      setValues((v) => ({ ...v, [field]: value }))
      setErrors((prev) => {
        if (!prev[field]) return prev
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  function chooseMethod(method) {
    setValues((v) => ({ ...v, method }))
    setErrors((prev) => {
      const next = { ...prev }
      delete next.drop_address
      delete next.city
      delete next.zip
      return next
    })
  }

  async function handleSubmit() {
    const found = validate(values, days)
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setState('sending')
    setMessage(null)

    try {
      await sendBookingRequest({
        trailer: `${trailer.unit} \u2014 ${trailer.name}`,
        ...values,
        days,
        quoted_total: formatUSD(quote.total),
        rate_note: quote.tier
          ? `${formatUSD(quote.perDay)}/day (${quote.tier})`
          : '',
      })
      setState('sent')
    } catch (err) {
      setState('error')
      setMessage(err.message)
    }
  }

  function field(name, label, input) {
    return (
      <div className={`field ${errors[name] ? 'field--error' : ''}`}>
        <label htmlFor={name}>{label}</label>
        {input}
        {errors[name] && (
          <span className="field__error" role="alert">
            {errors[name]}
          </span>
        )}
      </div>
    )
  }

  if (state === 'sent') {
    return (
      <section className="section request">
        <div className="wrap">
          <span className="eyebrow eyebrow--dark">Request sent</span>
          <h2>We&rsquo;ve got it</h2>
          <p className="hero__sub">
            {values.name || 'Thanks'} &mdash; we&rsquo;ll text you to confirm{' '}
            {trailer.unit} is free for those dates
            {delivering
              ? ' and lock in your delivery window.'
              : ', along with the pickup address and gate details.'}
          </p>
          <p className="hero__terms">
            Nothing is booked until we confirm. Need it sooner, call{' '}
            <a href={SITE.phoneHref}>{SITE.phone}</a>.
          </p>
          <div className="hero__actions">
            <Link className="btn btn--ghost" to="/">
              Back to the fleet
            </Link>
            <a className="btn btn--primary" href={SITE.phoneHref}>
              Call {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="section request">
      <div className="wrap">
        <Link className="backlink" to="/">
          &larr; All trailers
        </Link>

        <div className="reserve">
          <div className="reserve__form">
            <span className="eyebrow eyebrow--dark">
              {trailer.unit} &middot; {trailer.model}
            </span>
            <h2>Request the {trailer.name}</h2>
            <p className="hero__sub">
              Send your dates and we&rsquo;ll text back to confirm the unit is
              free. Payment is due when you take the trailer &mdash; nothing to
              pay now.
            </p>

            <div className="form" style={{ marginTop: '2rem' }}>
              <fieldset className="choice">
                <legend>How do you want it?</legend>
                <div className="choice__options">
                  <button
                    type="button"
                    className={`choice__btn ${!delivering ? 'is-on' : ''}`}
                    onClick={() => chooseMethod('pickup')}
                    aria-pressed={!delivering}
                  >
                    <strong>I&rsquo;ll pick it up</strong>
                    <span>Free &middot; 24/7 lot access</span>
                  </button>
                  <button
                    type="button"
                    className={`choice__btn ${delivering ? 'is-on' : ''}`}
                    onClick={() => chooseMethod('delivery')}
                    aria-pressed={delivering}
                  >
                    <strong>Deliver it</strong>
                    <span>Flat fee by distance</span>
                  </button>
                </div>
              </fieldset>

              <div className="form__row form__row--2">
                {field(
                  'start_date',
                  delivering ? 'Drop-off' : 'Pickup',
                  <input
                    id="start_date"
                    type="date"
                    min={today()}
                    value={values.start_date}
                    onChange={update('start_date')}
                  />,
                )}
                {field(
                  'end_date',
                  'Return',
                  <input
                    id="end_date"
                    type="date"
                    min={values.start_date || today()}
                    value={values.end_date}
                    onChange={update('end_date')}
                  />,
                )}
              </div>

              <div className="form__row form__row--2">
                {field(
                  'name',
                  'Your name',
                  <input
                    id="name"
                    value={values.name}
                    onChange={update('name')}
                    autoComplete="name"
                  />,
                )}
                {field(
                  'company',
                  'Company (optional)',
                  <input
                    id="company"
                    value={values.company}
                    onChange={update('company')}
                    autoComplete="organization"
                  />,
                )}
              </div>

              <div className="form__row form__row--2">
                {field(
                  'phone',
                  'Mobile',
                  <input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    value={values.phone}
                    onChange={update('phone')}
                    autoComplete="tel"
                  />,
                )}
                {field(
                  'email',
                  'Email',
                  <input
                    id="email"
                    type="email"
                    value={values.email}
                    onChange={update('email')}
                    autoComplete="email"
                  />,
                )}
              </div>

              {delivering && (
                <>
                  {field(
                    'drop_address',
                    'Job site address',
                    <input
                      id="drop_address"
                      value={values.drop_address}
                      onChange={update('drop_address')}
                      placeholder="Street address"
                      autoComplete="address-line1"
                    />,
                  )}

                  <div className="form__row form__row--2">
                    {field(
                      'city',
                      'City',
                      <input
                        id="city"
                        value={values.city}
                        onChange={update('city')}
                        autoComplete="address-level2"
                      />,
                    )}
                    {field(
                      'zip',
                      'ZIP',
                      <input
                        id="zip"
                        inputMode="numeric"
                        maxLength={5}
                        value={values.zip}
                        onChange={update('zip')}
                        autoComplete="postal-code"
                      />,
                    )}
                  </div>
                </>
              )}

              {field(
                'notes',
                delivering ? 'Where to set it (optional)' : 'Anything else (optional)',
                <textarea
                  id="notes"
                  value={values.notes}
                  onChange={update('notes')}
                  placeholder={
                    delivering
                      ? 'Gate code, driveway side, what you are loading'
                      : 'What you are hauling, what time you want it'
                  }
                />,
              )}

              {state === 'error' && (
                <div className="form__status form__status--error" role="alert">
                  {message} Call {SITE.phone} and we&rsquo;ll take it down over
                  the phone.
                </div>
              )}
            </div>
          </div>

          <aside className="quote">
            <div className="quote__head">
              <span>Your quote</span>
              <span>
                {days > 0 ? `${days} day${days > 1 ? 's' : ''}` : '\u2014'}
              </span>
            </div>

            {days > 0 ? (
              <>
                <dl className="quote__lines">
                  {quote.breakdown.map((row) => (
                    <div className="quote__line" key={row.label}>
                      <dt>{row.label}</dt>
                      <dd>{formatUSD(row.subtotal)}</dd>
                    </div>
                  ))}
                  <div className="quote__line quote__line--total">
                    <dt>Rental total</dt>
                    <dd>{formatUSD(quote.total)}</dd>
                  </div>
                </dl>

                <div className="quote__split">
                  <div className="quote__line quote__line--now">
                    <dt>{delivering ? 'Rental' : 'Total'}</dt>
                    <dd>{formatUSD(quote.total)}</dd>
                  </div>
                  <div className="quote__line">
                    <dt>{delivering ? 'Delivery' : 'Pickup'}</dt>
                    <dd>{delivering ? 'By distance' : 'Free'}</dd>
                  </div>
                </div>

                <p className="quote__note">
                  {delivering
                    ? `Delivery is quoted from our lot and collected with the rental. ${RATES.delivery[1].label}: ${RATES.delivery[1].fee}.`
                    : 'Pick up at our lot any hour. We text you the address and gate details once your dates are confirmed.'}
                </p>
              </>
            ) : (
              <p className="quote__empty">
                Pick your dates and the price shows up here. Longer rentals
                prorate at the best rate that fits &mdash; you never pay more
                for keeping it longer.
              </p>
            )}

            <button
              type="button"
              className="btn btn--primary btn--wide"
              onClick={handleSubmit}
              disabled={state === 'sending' || days < 1}
            >
              {state === 'sending' ? 'Sending\u2026' : 'Email me my invoice'}
            </button>

            <p className="form__note">
              We confirm the unit is free, then send your invoice.
            </p>

            <figure className="reserve__photo">
              <PhotoSlider
                images={trailer.images ?? [trailer.image]}
                alt={`${trailer.unit} \u2014 ${trailer.name}`}
              />
            </figure>
          </aside>
        </div>
      </div>
    </section>
  )
}
